import { useEffect, useRef } from 'react';

/**
 * LO ESCRITO A MEDIAS, GUARDADO EN EL SERVIDOR (26-10).
 *
 * Lo que el cliente deja a medias del check-in diario se envía solo a las 23:59 de su día
 * (`core/envio_de_borradores` del servidor), así que lo contestado no puede quedarse solo
 * en la pantalla:
 *
 * - `programar()` tras cada cambio: se manda al rato (`espera`), con lo último.
 * - En FILA, como la cola de Nutrición: con la red lenta, un guardado viejo que llegara
 *   detrás del último dejaría el borrador con respuestas de antes.
 * - Al salir de la pantalla, cerrar la pestaña o pasar a otra app con un cambio sin mandar,
 *   se manda ya con `keepalive` (el de Nutrición y el rastro), que sobrevive a la salida.
 *   Ese no espera a la fila: la pestaña se va y no habría «después».
 * - `guardarYa()` devuelve la promesa (para el botón «Guardar»); `cancelar()`, para cuando
 *   lo que iba al borrador ya viaja en el envío.
 *
 * - Lo mismo que ya se guardó no se vuelve a mandar (al abrir la pantalla, o con el doble
 *   montaje de React en desarrollo, saldría un guardado sin que haya cambiado nada).
 *
 * `cuerpo` es una función: se llama al mandar, para que vaya lo último que hay en pantalla.
 */
export default function useBorradorEnElServidor(api, ruta, cuerpo, { espera = 600 } = {}) {
    const pendiente = useRef(null);
    const fila = useRef(Promise.resolve());
    const yaGuardado = useRef(null);
    const ultimo = useRef({ api, ruta, cuerpo });
    ultimo.current = { api, ruta, cuerpo };

    const fallo = (err) => console.error(`No se pudo guardar lo escrito a medias (${ruta}):`,
        err?.response?.data || err);

    const cancelar = () => {
        clearTimeout(pendiente.current);
        pendiente.current = null;
    };

    const guardarYa = ({ keepalive = false } = {}) => {
        cancelar();
        const { api: a, ruta: r, cuerpo: c } = ultimo.current;
        const datos = c();
        const huella = JSON.stringify(datos);
        const mandar = () => (huella === yaGuardado.current ? Promise.resolve(null)
            : a.put(r, datos, keepalive ? { adapter: 'fetch', fetchOptions: { keepalive: true } } : undefined)
                .then((res) => { yaGuardado.current = huella; return res; }));
        if (keepalive) return mandar().catch(fallo);
        const esta = fila.current.then(mandar);
        fila.current = esta.catch(() => {});
        return esta;
    };

    const programar = () => {
        clearTimeout(pendiente.current);
        pendiente.current = setTimeout(() => { guardarYa().catch(fallo); }, espera);
    };

    const guardarYaRef = useRef(guardarYa);
    guardarYaRef.current = guardarYa;
    useEffect(() => {
        const alSalir = () => { if (pendiente.current) guardarYaRef.current({ keepalive: true }); };
        const alOcultarse = () => { if (document.visibilityState === 'hidden') alSalir(); };
        window.addEventListener('pagehide', alSalir);
        document.addEventListener('visibilitychange', alOcultarse);
        return () => {
            window.removeEventListener('pagehide', alSalir);
            document.removeEventListener('visibilitychange', alOcultarse);
            alSalir();
        };
    }, []);

    return { programar, guardarYa, cancelar };
}
